import { Outlet, Link, useLocation } from "react-router-dom";
import { Sparkles, Menu, X, Mail, Phone, MapPin } from "lucide-react";
import { FaFacebookF, FaXTwitter, FaInstagram, FaLinkedinIn } from "react-icons/fa6";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "../ui/button";

export function MainLayout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isActive = (path: string) =>
    location.pathname === path ? "text-[#2E5C8A]" : "text-gray-600";

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#5B9BD5] to-[#2E5C8A] rounded-xl flex items-center justify-center transition-transform group-hover:scale-105">
                <Sparkles className="w-6 h-6 text-white" />
              </div>

              <div>
                <div className="text-xl font-extrabold text-[#2E5C8A] tracking-tight">
                  ASTRA
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#5B9BD5] font-semibold">
                  Do. Build. Imagine.
                </div>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className={`font-medium transition hover:text-[#2E5C8A] ${isActive("/")}`}>
                Home
              </Link>
              <Link to="/programs" className={`font-medium transition hover:text-[#2E5C8A] ${isActive("/programs")}`}>
                Programs
              </Link>
              <Link to="/schools" className={`font-medium transition hover:text-[#2E5C8A] ${isActive("/schools")}`}>
                For Schools
              </Link>
              <Link to="/about" className={`font-medium transition hover:text-[#2E5C8A] ${isActive("/about")}`}>
                About Us
              </Link>

              <Button className="bg-gradient-to-r from-[#FFD700] to-[#FFC700] text-gray-900 hover:opacity-90 rounded-full px-6 border-none shadow-lg shadow-yellow-100">
                Partner With Us
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <button className="md:hidden p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
            >
              <div className="px-4 pt-4 pb-8 space-y-4">
                <Link to="/" className="block text-lg font-medium text-gray-900">Home</Link>
                <Link to="/programs" className="block text-lg font-medium text-gray-900">Programs</Link>
                <Link to="/schools" className="block text-lg font-medium text-gray-900">For Schools</Link>
                <Link to="/about" className="block text-lg font-medium text-gray-900">About Us</Link>

                <Button className="w-full bg-[#FFD700] text-gray-900 rounded-full py-6 text-lg">
                  Partner With Us
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#0A1A2F] text-white pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-[#5B9BD5] to-[#2E5C8A] rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-bold text-xl">ASTRA</div>
                  <div className="text-xs text-[#FFD700] uppercase tracking-widest">
                    Do. Build. Imagine.
                  </div>
                </div>
              </div>

              <p className="text-gray-400 leading-relaxed mb-6">
                Astra Coding and Robotics International nurtures innovation,
                problem-solving and creativity among young learners through
                coding, robotics and hands-on STEAM learning.
              </p>

              <div className="flex gap-4">
                <button type="button" aria-label="Facebook" className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center hover:bg-[#5B9BD5] transition-colors"><FaFacebookF className="w-4 h-4" /></button>
                <button type="button" aria-label="Twitter" className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center hover:bg-[#5B9BD5] transition-colors"><FaXTwitter className="w-4 h-4" /></button>
                <button type="button" aria-label="Instagram" className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center hover:bg-[#5B9BD5] transition-colors"><FaInstagram className="w-4 h-4" /></button>
                <button type="button" aria-label="LinkedIn" className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center hover:bg-[#5B9BD5] transition-colors"><FaLinkedinIn className="w-4 h-4" /></button>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-6 text-[#00FFFF]">Quick Links</h4>
              <ul className="space-y-4 text-gray-400">
                <li><Link to="/" className="hover:text-white transition">Home</Link></li>
                <li><Link to="/programs" className="hover:text-white transition">Our Programs</Link></li>
                <li><Link to="/schools" className="hover:text-white transition">School Partnerships</Link></li>
                <li><Link to="/about" className="hover:text-white transition">About Astra</Link></li>
                <li><button type="button" className="hover:text-white transition text-left">Privacy Policy</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-6 text-[#FFD700]">Programs</h4>
              <ul className="space-y-4 text-gray-400">
                <li><Link to="/programs" className="hover:text-white transition">Coding Fundamentals</Link></li>
                <li><Link to="/programs" className="hover:text-white transition">Robotics with LEGO Mindstorms</Link></li>
                <li><Link to="/programs" className="hover:text-white transition">Arduino-Based Robotics</Link></li>
                <li><Link to="/programs" className="hover:text-white transition">Computer Literacy</Link></li>
                <li><Link to="/schools" className="hover:text-white transition">Teacher Training & Equipment Supply</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-6 text-[#00FFFF]">Contact Us</h4>
              <ul className="space-y-4">
                <li className="flex gap-3 text-gray-400">
                  <MapPin className="w-5 h-5 text-[#5B9BD5] flex-shrink-0" />
                  <span>Nairobi, Kenya</span>
                </li>
                <li className="flex gap-3 text-gray-400">
                  <Phone className="w-5 h-5 text-[#5B9BD5] flex-shrink-0" />
                  <span>0712 890 791</span>
                </li>
                <li className="flex gap-3 text-gray-400">
                  <Mail className="w-5 h-5 text-[#5B9BD5] flex-shrink-0" />
                  <span>astraroboticsandprogramming@gmail.com</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 text-center text-gray-500 text-sm">
            <p>
              © {new Date().getFullYear()} Astra Coding and Robotics International.
              All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}